import dayjs from 'dayjs';
import {
  TProgressItem,
  TResourceProgress,
  FileStatus,
  TDownloadListItem,
} from '~/store/resource/resource.type';
import { TDownloadableResource } from '~/types/common.type';
import { UpdateStatus } from '~/types/enum';
import { maybeFixUtf8Mojibake } from '~/utils/helper/encoding';
import { getFileUpdateStatus } from '~/utils/helper/function';

type TDownloadFile = {
  url: string;
  name: string;
};

const NATIVE_DOWNLOAD_EXT = new Set(['dmg', 'iso', 'pkg', 'zip']);

const getExtension = (value: string): string => {
  const clean = String(value ?? '').split(/[?#]/)[0];
  const match = clean.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : '';
};

const nativeDownload = (url: string, normalizedName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = normalizedName;
  link.rel = 'noreferrer';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const blobDownload = (url: string, normalizedName: string) => {
  fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      return response.blob();
    })
    .then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.rel = 'noreferrer';
      link.download = normalizedName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    })
    .catch(error => {
      console.error('下載檔案時發生錯誤：', error);
      nativeDownload(url, normalizedName);
    });
};
export const forceDownload = ({ url, name }: TDownloadFile) => {
  const normalizedName = maybeFixUtf8Mojibake(String(name ?? '').trim());
  const ext = getExtension(name) || getExtension(url);

  if (NATIVE_DOWNLOAD_EXT.has(ext)) {
    nativeDownload(url, normalizedName);
  } else {
    blobDownload(url, normalizedName);
  }
};

type TCreateProgressInfo = {
  progressItem?: TProgressItem;
  downloadItem?: TDownloadListItem;
};

export const createProgressInfo = ({
  progressItem,
  downloadItem,
}: TCreateProgressInfo): TResourceProgress => {
  return {
    key: downloadItem
      ? downloadItem.path + '.' + downloadItem.fileFormat
      : undefined,
    status: downloadItem?.status || FileStatus.non,
    fileFormat: downloadItem?.fileFormat,
    filePath: downloadItem?.path,
    progressRatio: Math.round(
      downloadItem?.status === FileStatus.finish
        ? 100
        : progressItem?.progress || 0,
    ),
    speedBytes: progressItem?.speedBytes || 0,
    speed: progressItem?.speed || '-',
    totalBytes: progressItem
      ? progressItem.totalBytes
      : downloadItem
        ? downloadItem.fileSize
        : 0,
    total: progressItem?.total || '-',
    downloaded: progressItem?.downloaded || '-',
    downloadedBytes: progressItem
      ? progressItem.downloadedBytes
      : downloadItem
        ? downloadItem.fileSize
        : 0,
    updateTime: downloadItem?.updateTime || dayjs().valueOf(),
    installingProgressRatio: downloadItem?.installingProgress || 0,
    downloadedAt: downloadItem?.downloadedAt,
  };
};

export type TProgressLookup = (url: string) => TResourceProgress;

/**
 * 下載狀態只看檔案 url，不能跟著資源清單走。資源頁的清單是無限捲動一頁頁累積的快照，
 * 以前改去「目前頁碼」的查詢結果裡找，載入下一頁後前面頁面的資源就找不到，
 * 已下載的檔案一直顯示「下載」、再按還會重複下載（NBX3-1117）。
 *
 * 同一份清單下快取每個 url 的結果：沒有下載紀錄時 createProgressInfo 以當下時間當
 * updateTime，不快取的話每次渲染拿到的都是不同的值。
 */
export const createProgressLookup = (
  progressList: TProgressItem[],
  downloadList: TDownloadListItem[],
): TProgressLookup => {
  const cache = new Map<string, TResourceProgress>();
  return url => {
    let progress = cache.get(url);
    if (!progress) {
      progress = createProgressInfo({
        progressItem: progressList.find(item => item.url === url),
        downloadItem: downloadList.find(item => item.url === url),
      });
      cache.set(url, progress);
    }
    return progress;
  };
};

export const withLiveProgress = <T extends TDownloadableResource>(
  items: T[],
  lookup: TProgressLookup,
): T[] =>
  items.map(item => ({ ...item, progress: lookup(item.resource.file.url) }));

/**
 * 單檔下載列表可以勾選批次下載的條件：還沒下載，或雲端版本比已下載的新
 */
export const canSelectForDownload = ({
  progress,
  resource,
}: TDownloadableResource) =>
  progress.status === FileStatus.non ||
  getFileUpdateStatus({
    downloadedAt: progress.downloadedAt,
    uploadedAt: resource.file.fileUploadTime,
  }) === UpdateStatus.old;

/**
 * 列表的下載狀態即時更新後，勾選後才開始下載的項目會失去勾選框，留在已選取裡的話
 * 批次下載會再送一次（NBX3-1117）。沒有要移除的就回傳原陣列，交給 setState 時不會多渲染一次。
 */
export const pruneUnselectable = <T extends TDownloadableResource>(
  selected: T[],
  currentList: TDownloadableResource[],
): T[] => {
  const next = selected.filter(item => {
    const current = currentList.find(
      ({ resource }) => resource.file.url === item.resource.file.url,
    );
    return !!current && canSelectForDownload(current);
  });
  return next.length === selected.length ? selected : next;
};
